<template>
  <div class="no-tabs sent-geo-modal">
    <div class="mb-2 d-flex justify-content-between align-items-center">
      <div>
        <button
          v-if="hasFilters"
          class="btn btn-primary btn-sm me-2"
          @click="clearFilters"
        >
          {{ $t('modules.users.clear_filters') }}
        </button>
      </div>
      <button
        class="btn btn-success btn-sm"
        @click="createUser"
      >
        <i class="bi bi-plus" />
        {{ $t('modules.users.new_head') }}
      </button>
    </div>

    <DxDataGrid
      :ref="gridRef"
      :data-source="store"
      :hover-state-enabled="true"
      :remote-operations="true"
      :column-auto-width="true"
      :show-borders="true"
      :scrolling="scrollingConfig"
      @optionChanged="detectFilters"
    >
      <DxHeaderFilter :visible="true" />
      <DxFilterRow :visible="true" />
      <DxPaging :page-size="10" />
      <DxPager
        :show-info="true"
        :show-navigation-buttons="true"
        :visible="true"
      />

      <DxColumn
        data-field="name"
        :caption="$t('modules.users.name')"
      />
      <DxColumn
        data-field="surname"
        :caption="$t('modules.users.surname')"
      />
      <DxColumn
        data-field="email"
        :caption="$t('modules.users.email')"
      />
      <DxColumn
        :caption="$t('modules.users.roles')"
        :calculate-cell-value="row =>
          row.roles
            ?.map(r => this.$t(`modules.users.user_roles.${r}`))
            .join(', ')
        "
      />
      <DxColumn
        data-field="phone"
        :caption="$t('modules.users.phone')"
      />
      <DxColumn
        data-field="driver_card"
        :caption="$t('modules.users.driver_card')"
      />
      <DxColumn
        :caption="$t('modules.users.driver_card_validity')"
        :calculate-cell-value="formatDriverCardValidity"
      />
      <DxColumn
        data-field="active"
        :caption="$t('modules.users.active')"
        data-type="boolean"
      />

      <DxColumn type="buttons">
        <DxButton
          icon="edit"
          @click="edit"
        />
        <DxButton
          icon="remove"
          @click="remove"
        />
      </DxColumn>
    </DxDataGrid>
  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue';
import {getStore, deleteMany} from './crud';
import {
  DxDataGrid,
  DxColumn,
  DxButton,
  DxFilterRow,
  DxHeaderFilter,
  DxPaging,
  DxPager,
} from 'devextreme-vue/data-grid';
import {useQuasar} from 'quasar';
import {getHumanDate} from '@/utils/tools';

export default defineComponent({
  name: 'UsersList',
  components: {
    DxDataGrid,
    DxColumn,
    DxButton,
    DxFilterRow,
    DxHeaderFilter,
    DxPaging,
    DxPager,
  },
  data() {
    return {
      store: getStore(),
      gridRef: 'usersGrid',
      hasFilters: false,
      windowWidth: window.innerWidth,
    };
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  setup() {
    return {quasar: useQuasar()};
  },
  computed: {
    grid() {
      // @ts-ignore
      return this.$refs[this.gridRef]?.instance;
    },
    isMobile() {
      return this.windowWidth <= 767;
    },
    scrollingConfig() {
      return {
        mode: this.isMobile ? 'virtual' : 'standard',
        rowRenderingMode: this.isMobile ? 'virtual' : 'standard',
        columnRenderingMode: this.isMobile ? 'virtual' : 'standard',
        useNativeScrolling: this.isMobile,
        scrollByContent: this.isMobile,
        scrollByThumb: this.isMobile,
        showScrollbar: this.isMobile ? 'always' : 'onHover',
        x: this.isMobile ? 'hidden' : 'auto'
      };
    },
  },
  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    formatDriverCardValidity(row: any) {
      const val = row.driver_card_validity;
      if (!val) {
        return '';
      }
      const ms = new Date(val).getTime();
      const ts = Math.floor(ms / 1000);
      return getHumanDate(ts);
    },
    detectFilters() {
      this.hasFilters = !!this.grid?.getCombinedFilter();
    },
    clearFilters() {
      this.grid?.clearFilter();
    },
    createUser() {
      this.$router.push('/users/create');
    },
    edit(e: any) {
      const id = e.row.key;
      this.$router.push(`/users/${id}/edit`);
    },
    remove(e: any) {
      const email = e.row.data.email;
      this.quasar
          .dialog({
            title: this.$t('modules.users.delete_confirm_title'),
            message: `${this.$t('modules.users.delete_confirm')} <br><b>${email}</b>`,
            html: true,
            cancel: true,
          })
          .onOk(async () => {
            try {
              await deleteMany([email]);
              this.$toast.success(this.$t('modules.users.delete') as string)
            } catch (err: any) {
              const key = err.message || 'modules.users.delete_error'
              this.$toast.error(this.$t(key) as string)
            }
            this.grid?.refresh();
          });
    },
  },
});
</script>
